import React, { useState } from 'react';
import { Field } from 'redux-form';
import { Row, Col, FormGroup } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import messages from './messages';
import PhoneNumber from '../PhoneNumber';
import {
  renderTextField,
  renderEmailField,
  renderSelectField,
} from '../../../../utils/redux-form-helper';
import { useFlags } from 'launchdarkly-react-client-sdk';

type Props = {
  intl: any,
  suffixes?: Array<Object>,
  firstName?: string,
  onActivate?: Function,
  message?: Object,
  onClick?: Function,
  change?: Function,
  formName?: string,
};

export const FirstName = ({ intl }: Props) => {
  return (
    <Col xs={6} sm={3}>
      <Field
        className="input-lg"
        name="firstName"
        component={renderTextField}
        placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
        maxLength="35"
      />
    </Col>
  );
};

export const MiddleName = ({ intl }: Props) => {
  return (
    <Col xs={6} sm={3}>
      <Field
        className="input-lg"
        name="middleName"
        component={renderTextField}
        placeholder={intl.formatMessage(messages.middleNamePlaceholder)}
        maxLength="35"
      />
    </Col>
  );
};

export const LastName = ({ intl }: Props) => {
  return (
    <Col xs={6} sm={3}>
      <Field
        className="input-lg"
        name="lastName"
        component={renderTextField}
        placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
        maxLength="35"
      />
    </Col>
  );
};

export const PreferredName = ({ intl }: Props) => {
  return (
    <Col xs={6} sm={3}>
      <Field
        className="input-lg"
        name="preferredName"
        component={renderTextField}
        placeholder={intl.formatMessage(messages.preferredNamePlaceholder)}
        maxLength="35"
      />
    </Col>
  );
};

export const Suffix = ({ intl, suffixes }: Props) => {
  return (
    <Col xs={6} sm={3}>
      <Field
        selectClassNames="input-lg"
        name="suffixId"
        component={renderSelectField}
        placeholder={intl.formatMessage(messages.suffixPlaceholder)}
        options={suffixes}
        maxLength="35"
      />
    </Col>
  );
};

export const AddContactInfoOption = ({ intl, message, onClick }: Props) => {
  return (
    <a className="btn-text" onClick={onClick}>
      <FormattedMessage {...message} />
    </a>
  );
};

const DeleteButton = ({ onClick, intl }) => (
  <div className="row-remove-btn">
    <a onClick={onClick} style={{ cursor: 'pointer' }}>
      <i className="et-trash" />
    </a>
  </div>
);

export const SecondaryPhone = ({
  intl,
  showField,
  phoneTypes = [],
  onChange,
  onAdd,
  onDelete,
  showDeleteButton,
}) => {
  return (
    <Col xs={6} sm={6}>
      {showField && (
        <>
          <Col xs={0} sm={12}>
            <label>
              <FormattedMessage {...messages.secondaryPhoneLabel} />
            </label>
          </Col>
          <Col xs={6} sm={6}>
            <PhoneNumber intl={intl} fieldName="secondaryPhoneNumber" />
          </Col>
          <Col xs={6} sm={5}>
            <Field
              selectClassNames="input-lg"
              name="secondaryPhoneType"
              component={renderSelectField}
              options={phoneTypes}
              onChange={onChange}
            />
          </Col>
          <Col xs={1} sm={1} style={{ marginTop: '10px' }}>
            {showDeleteButton && (
              <DeleteButton onClick={onDelete} intl={intl} />
            )}
          </Col>
        </>
      )}
      {!showField && (
        <AddContactInfoOption
          intl={intl}
          message={{ ...messages.addPhone }}
          onClick={onAdd}
        />
      )}
    </Col>
  );
};

export const TertiaryPhone = ({
  intl,
  showField,
  phoneTypes = [],
  onChange,
  onAdd,
  onDelete,
  showDeleteButton,
}) => {
  return (
    <Col xs={6} sm={6}>
      {showField && (
        <>
          <Col xs={0} sm={12}>
            <label>
              <FormattedMessage {...messages.tertiaryPhoneLabel} />
            </label>
          </Col>
          <Col xs={6} sm={6}>
            <PhoneNumber intl={intl} fieldName="tertiaryPhoneNumber" />
          </Col>
          <Col xs={6} sm={5}>
            <Field
              selectClassNames="input-lg"
              name="tertiaryPhoneType"
              component={renderSelectField}
              options={phoneTypes}
              onChange={onChange}
            />
          </Col>
          <Col xs={1} sm={1} style={{ marginTop: '10px' }}>
            {showDeleteButton && (
              <DeleteButton onClick={onDelete} intl={intl} />
            )}
          </Col>
        </>
      )}
      {!showField && (
        <AddContactInfoOption
          intl={intl}
          message={{ ...messages.addPhone }}
          onClick={onAdd}
        />
      )}
    </Col>
  );
};

export const SecondaryEmail = ({
  intl,
  showField,
  emailTypes = [],
  onChange,
  onAdd,
  onDelete,
  showDeleteButton,
}) => {
  return (
    <Col xs={6} sm={6}>
      {showField && (
        <>
          <Col xs={0} sm={12}>
            <label>
              <FormattedMessage {...messages.secondaryEmailLabel} />
            </label>
          </Col>
          <Col xs={6} sm={6}>
            <Field
              className="input-lg"
              name="secondaryEmailAddress"
              component={renderEmailField}
              placeholder={intl.formatMessage(messages.emailPlaceholder)}
              label={intl.formatMessage(messages.emailLabel)}
              maxLength="255"
            />
          </Col>
          <Col xs={6} sm={5}>
            <Field
              selectClassNames="input-lg"
              name="secondaryEmailType"
              component={renderSelectField}
              options={emailTypes}
              onChange={onChange}
            />
          </Col>
          <Col xs={1} sm={1} style={{ marginTop: '10px' }}>
            {showDeleteButton && (
              <DeleteButton onClick={onDelete} intl={intl} />
            )}
          </Col>
        </>
      )}
      {!showField && (
        <AddContactInfoOption
          intl={intl}
          message={{ ...messages.addEmail }}
          onClick={onAdd}
        />
      )}
    </Col>
  );
};

export const TertiaryEmail = ({
  intl,
  showField,
  emailTypes = [],
  onChange,
  onAdd,
  onDelete,
  showDeleteButton,
}) => {
  return (
    <Col xs={6} sm={6}>
      {showField && (
        <>
          <Col xs={0} sm={12}>
            <label>
              <FormattedMessage {...messages.tertiaryEmailLabel} />
            </label>
          </Col>
          <Col xs={6} sm={6}>
            <Field
              className="input-lg"
              name="tertiaryEmailAddress"
              component={renderEmailField}
              placeholder={intl.formatMessage(messages.emailPlaceholder)}
              label={intl.formatMessage(messages.emailLabel)}
              maxLength="255"
            />
          </Col>
          <Col xs={6} sm={5}>
            <Field
              selectClassNames="input-lg"
              name="tertiaryEmailType"
              component={renderSelectField}
              options={emailTypes}
              onChange={onChange}
            />
          </Col>
          <Col xs={1} sm={1} style={{ marginTop: '10px' }}>
            {showDeleteButton && (
              <DeleteButton onClick={onDelete} intl={intl} />
            )}
          </Col>
        </>
      )}
      {!showField && (
        <AddContactInfoOption
          intl={intl}
          message={{ ...messages.addEmail }}
          onClick={onAdd}
        />
      )}
    </Col>
  );
};

export const AddContactDetailsOption = ({
  intl,
  change,
  formName,
  phoneTypes,
  emailTypes,
}: Props) => {
  const { additionalPhoneAndEmailFieldsForLeadProfile } = useFlags();

  const [showSecondaryPhone, setShowSecondaryPhone] = useState(false);
  const [showTertiaryPhone, setShowTertiaryPhone] = useState(false);
  const [showSecondaryEmail, setShowSecondaryEmail] = useState(false);
  const [showTertiaryEmail, setShowTertiaryEmail] = useState(false);

  const handleAddSecondaryPhone = () => {
    setShowSecondaryPhone(true);
    if (change && formName) {
      change('secondaryPhoneType', phoneTypes[0].value);
    }
  };
  const handleDeleteSecondaryPhone = () => {
    setShowSecondaryPhone(false);
    setShowTertiaryPhone(false);
    if (change && formName) {
      change('secondaryPhoneNumber', null);
      change('secondaryPhoneType', null);
      change('tertiaryPhoneNumber', null);
      change('tertiaryPhoneType', null);
    }
  };
  const handleAddTertiaryPhone = () => {
    setShowTertiaryPhone(true);
    if (change && formName) {
      change('tertiaryPhoneType', phoneTypes[0].value);
    }
  };
  const handleDeleteTertiaryPhone = () => {
    setShowTertiaryPhone(false);
    if (change && formName) {
      change('tertiaryPhoneNumber', null);
      change('tertiaryPhoneType', null);
    }
  };

  const handleAddSecondaryEmail = () => {
    setShowSecondaryEmail(true);
    if (change && formName) {
      change('secondaryEmailType', emailTypes[0].value);
    }
  };
  const handleDeleteSecondaryEmail = () => {
    setShowSecondaryEmail(false);
    setShowTertiaryEmail(false);
    if (change && formName) {
      change('secondaryEmailAddress', null);
      change('secondaryEmailType', null);
      change('tertiaryEmailAddress', null);
      change('tertiaryEmailType', null);
    }
  };
  const handleAddTertiaryEmail = () => {
    setShowTertiaryEmail(true);
    if (change && formName) {
      change('tertiaryEmailType', emailTypes[0].value);
    }
  };
  const handleDeleteTertiaryEmail = () => {
    setShowTertiaryEmail(false);
    if (change && formName) {
      change('tertiaryEmailAddress', null);
      change('tertiaryEmailType', null);
    }
  };

  const phoneAndEmailContainerWidth =
    additionalPhoneAndEmailFieldsForLeadProfile ? 6 : 3;
  const phoneAndEmailFieldWidth = additionalPhoneAndEmailFieldsForLeadProfile
    ? 6
    : 12;

  return (
    <>
      <Row>
        <div className="container-of-inputs">
          <PreferredName intl={intl} />
          <Col xs={6} sm={9}>
            <Row style={{ marginLeft: '-3px', marginRight: 0 }}>
              <Col
                xs={phoneAndEmailContainerWidth}
                sm={phoneAndEmailContainerWidth}
              >
                <Col xs={phoneAndEmailFieldWidth} sm={phoneAndEmailFieldWidth}>
                  <PhoneNumber intl={intl} fieldName="phoneNumber" />
                </Col>
                {additionalPhoneAndEmailFieldsForLeadProfile && (
                  <Col xs={6} sm={6}>
                    <Field
                      selectClassNames="input-lg"
                      name="phoneType"
                      component={renderSelectField}
                      options={phoneTypes}
                    />
                  </Col>
                )}
              </Col>
              <Col
                xs={phoneAndEmailContainerWidth}
                sm={phoneAndEmailContainerWidth}
              >
                <Col xs={phoneAndEmailFieldWidth} sm={phoneAndEmailFieldWidth}>
                  <Field
                    className="input-lg"
                    name="emailAddress"
                    component={renderEmailField}
                    placeholder={intl.formatMessage(messages.emailPlaceholder)}
                    label={intl.formatMessage(messages.emailLabel)}
                    maxLength="255"
                  />
                </Col>
                {additionalPhoneAndEmailFieldsForLeadProfile && (
                  <Col xs={6} sm={6}>
                    <Field
                      selectClassNames="input-lg"
                      name="emailType"
                      component={renderSelectField}
                      options={emailTypes}
                    />
                  </Col>
                )}
              </Col>
            </Row>
          </Col>
        </div>
      </Row>
      {additionalPhoneAndEmailFieldsForLeadProfile && (
        <Row>
          <div className="container-of-inputs">
            <Col xs={0} sm={3}></Col>
            <Col xs={6} sm={9}>
              <Row style={{ marginLeft: '-3px', marginRight: 0 }}>
                <SecondaryPhone
                  intl={intl}
                  showField={showSecondaryPhone}
                  phoneTypes={phoneTypes}
                  onAdd={handleAddSecondaryPhone}
                  onDelete={handleDeleteSecondaryPhone}
                  showDeleteButton={showSecondaryPhone && !showTertiaryPhone}
                />
                <SecondaryEmail
                  intl={intl}
                  showField={showSecondaryEmail}
                  emailTypes={emailTypes}
                  onAdd={handleAddSecondaryEmail}
                  onDelete={handleDeleteSecondaryEmail}
                  showDeleteButton={showSecondaryEmail && !showTertiaryEmail}
                />
              </Row>
            </Col>
          </div>
        </Row>
      )}
      {additionalPhoneAndEmailFieldsForLeadProfile &&
        (showSecondaryPhone || showSecondaryEmail) && (
          <Row>
            <div className="container-of-inputs">
              <Col xs={0} sm={3}></Col>
              <Col xs={6} sm={9}>
                <Row style={{ marginLeft: '-3px', marginRight: 0 }}>
                  {showSecondaryPhone ? (
                    <TertiaryPhone
                      intl={intl}
                      showField={showTertiaryPhone}
                      phoneTypes={phoneTypes}
                      onAdd={handleAddTertiaryPhone}
                      onDelete={handleDeleteTertiaryPhone}
                      showDeleteButton={showTertiaryPhone}
                    />
                  ) : (
                    <Col xs={6} sm={6} />
                  )}
                  {showSecondaryEmail ? (
                    <TertiaryEmail
                      intl={intl}
                      showField={showTertiaryEmail}
                      emailTypes={emailTypes}
                      onAdd={handleAddTertiaryEmail}
                      onDelete={handleDeleteTertiaryEmail}
                      showDeleteButton={showTertiaryEmail}
                    />
                  ) : (
                    <Col xs={6} sm={6} />
                  )}
                </Row>
              </Col>
            </div>
          </Row>
        )}
    </>
  );
};

const PrimaryContactInformation = ({
  intl,
  suffixes,
  firstName,
  change,
  formName,
  phoneTypes,
  emailTypes,
}: Props) => {
  const { additionalPhoneAndEmailFieldsForLeadProfile } = useFlags();
  const phoneAndEmailFieldWidth = additionalPhoneAndEmailFieldsForLeadProfile
    ? 6
    : 3;

  return (
    <FormGroup className="applicant-grp">
      <Row className="block-heading">
        <h2>
          <FormattedMessage {...messages.myNameIs} /> {firstName}
          <FormattedMessage {...messages.mayIAsk} />
        </h2>
      </Row>
      <Row>
        <div className="container-of-inputs">
          <Col xs={6} sm={3}>
            <label>
              <FormattedMessage {...messages.firstNameLabel} />
            </label>
          </Col>
          <Col xs={6} sm={3}>
            <label>
              <FormattedMessage {...messages.middleNameLabel} />
            </label>
          </Col>
          <Col xs={6} sm={3}>
            <label>
              <FormattedMessage {...messages.lastNameLabel} />
            </label>
          </Col>
          <Col xs={6} sm={3}>
            <label>
              <FormattedMessage {...messages.suffixLabel} />
            </label>
          </Col>
        </div>
      </Row>
      <Row>
        <div className="container-of-inputs">
          <FirstName intl={intl} />
          <MiddleName intl={intl} />
          <LastName intl={intl} />
          <Suffix intl={intl} suffixes={suffixes} />
        </div>
      </Row>
      <Row>
        <div className="container-of-inputs">
          <Col xs={6} sm={3}>
            <label>
              <FormattedMessage {...messages.preferredNameLabel} />
            </label>
          </Col>
          <Col xs={6} sm={9}>
            <Row style={{ marginLeft: '-3px', marginRight: 0 }}>
              <Col xs={phoneAndEmailFieldWidth} sm={phoneAndEmailFieldWidth}>
                <label>
                  <FormattedMessage {...messages.phoneLabel} />
                </label>
              </Col>
              <Col xs={phoneAndEmailFieldWidth} sm={phoneAndEmailFieldWidth}>
                <label>
                  <FormattedMessage {...messages.emailLabel} />
                </label>
              </Col>
            </Row>
          </Col>
          {/* <Col xs={6} sm={1} /> */}
        </div>
      </Row>

      <AddContactDetailsOption
        intl={intl}
        phoneTypes={phoneTypes}
        emailTypes={emailTypes}
        change={change}
        formName={formName}
      />
    </FormGroup>
  );
};

export default PrimaryContactInformation;
